import React, { memo } from 'react';
import { get } from 'lodash';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import formatPrice from 'services/helpers/formatPrice';
import { sanitizeRestProps } from 'services/helpers/sanitize';
import { getDomesticCurrency } from 'services/helpers/currency';
import { Skeleton } from 'components/layout/Skeleton';

const useStyles = makeStyles((theme) => ({
  error: {
    fontSize: '13px',
    color: '#FF0000',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorButton: {
    '&.MuiButtonBase-root.MuiButton-outlined': {
      color: '#FF0000',
      borderColor: 'rgba(255, 0, 0, 0.20)',
      padding: '4px 16px',
      textTransform: 'none',
    },
  },
}));

interface Props {
  record?: Record<string, any>;
  source?: string;
  fixedValue?: string | number;
  currency?: string;
  [key: string]: any;
  loading?: boolean;
  error?: string;
  onErrorFix?: () => void;
}

const PriceField = memo<Props>(
  ({
    record = {},
    source = 'total',
    fixedValue,
    currency,
    loading,
    error,
    onErrorFix,
    ...rest
  }) => {
    const classes = useStyles();

    if (loading) {
      return <Skeleton width="50px" height="35px" />;
    }

    if (error && onErrorFix) {
      return (
        <Box className={classes.errorContainer}>
          <Typography className={classes.error}>{error}</Typography>
          <Button variant="outlined" className={classes.errorButton} onClick={onErrorFix}>
            Retry
          </Button>
        </Box>
      );
    }

    if (error) {
      return <>-</>;
    }

    const price = fixedValue ?? get(record, source);
    const fieldCurrency = get(record, 'currency') || currency || getDomesticCurrency();

    return (
      <Typography variant="body1" data-test="price" {...sanitizeRestProps(rest)}>
        {formatPrice(price, { currency: fieldCurrency })}
      </Typography>
    );
  }
);

PriceField.displayName = 'PriceField';

export default PriceField;
